/**
 * Consolidated date utility functions for the FitFlow app
 * 
 * IMPORTANT: All dates are handled in Pacific/Auckland (NZ) timezone.
 * This file consolidates duplicate date helpers from across the codebase.
 */

const APP_TIMEZONE = 'Pacific/Auckland';
const APP_LOCALE = 'en-NZ';

/**
 * Format a date for display with various format options
 * Uses NZ locale for consistency across the app
 * 
 * @param {Date|string} date - Date object or date string
 * @param {string} format - Format type (see switch cases for options)
 * @returns {string} Formatted date string
 */
export function formatDate(date, format = 'default') {
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    switch (format) {
      // Time formats
      case 'time':
      case 'h:mm a':
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      
      // Date formats
      case 'date':
        return d.toLocaleDateString(APP_LOCALE, {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      
      case 'MMM dd':
        return d.toLocaleDateString(APP_LOCALE, { month: 'short', day: '2-digit' });
      
      case 'MMM dd, yyyy':
        return d.toLocaleDateString(APP_LOCALE, { month: 'short', day: '2-digit', year: 'numeric' });
      
      case 'MMM d, yyyy':
        return d.toLocaleDateString(APP_LOCALE, { month: 'short', day: 'numeric', year: 'numeric' });
      
      case 'EEEE, MMMM d, yyyy':
        return d.toLocaleDateString(APP_LOCALE, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
      
      case 'MMMM':
        return d.toLocaleDateString(APP_LOCALE, { month: 'long' });
      
      case 'yyyy':
        return d.toLocaleDateString(APP_LOCALE, { year: 'numeric' });
      
      case 'MMM yyyy':
        return d.toLocaleDateString(APP_LOCALE, { month: 'short', year: 'numeric' });
      
      case 'MMMM yyyy':
        return d.toLocaleDateString(APP_LOCALE, { month: 'long', year: 'numeric' });
      
      case 'MMM':
        return d.toLocaleDateString(APP_LOCALE, { month: 'short' });
      
      case 'EEE d':
        return d.toLocaleDateString(APP_LOCALE, { weekday: 'short', day: 'numeric' });
      
      case 'EEE':
        return d.toLocaleDateString(APP_LOCALE, { weekday: 'short' });
      
      // Date + Time formats
      case 'datetime':
        return d.toLocaleString(APP_LOCALE, {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      
      case 'MMM d, h:mm a':
        return `${d.toLocaleDateString(APP_LOCALE, { month: 'short', day: 'numeric' })}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      
      case 'MMM d, yyyy h:mm a':
        return `${d.toLocaleDateString(APP_LOCALE, { month: 'short', day: 'numeric', year: 'numeric' })} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      
      case 'h:mm a • MMM dd, yyyy':
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} • ${d.toLocaleDateString(APP_LOCALE, { month: 'short', day: '2-digit', year: 'numeric' })}`;
      
      // ISO-like formats (for form inputs)
      case 'yyyy-MM-dd':
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      
      case "yyyy-MM-dd'T'HH:mm":
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      
      default:
        return d.toLocaleString(APP_LOCALE);
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(date);
  }
}

/**
 * Check if a date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean}
 */
export function isToday(date) {
  const today = new Date();
  const d = new Date(date);
  return d.getFullYear() === today.getFullYear() &&
         d.getMonth() === today.getMonth() &&
         d.getDate() === today.getDate();
}

/**
 * Check if a date is tomorrow
 * @param {Date|string} date - Date to check
 * @returns {boolean}
 */
export function isTomorrow(date) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const d = new Date(date);
  return d.getFullYear() === tomorrow.getFullYear() &&
         d.getMonth() === tomorrow.getMonth() &&
         d.getDate() === tomorrow.getDate();
}

/**
 * Check if two dates are on the same day (in NZ timezone)
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {boolean}
 */
export function isSameDay(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  
  // Use Intl.DateTimeFormat to get date components in Pacific/Auckland timezone
  const formatter = new Intl.DateTimeFormat('en-CA', { 
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  const d1Parts = formatter.formatToParts(d1);
  const d2Parts = formatter.formatToParts(d2);
  
  const d1Date = `${d1Parts.find(p => p.type === 'year').value}-${d1Parts.find(p => p.type === 'month').value}-${d1Parts.find(p => p.type === 'day').value}`;
  const d2Date = `${d2Parts.find(p => p.type === 'year').value}-${d2Parts.find(p => p.type === 'month').value}-${d2Parts.find(p => p.type === 'day').value}`;
  
  return d1Date === d2Date;
}

/**
 * Add days to a date
 * @param {Date|string} date - Starting date
 * @param {number} days - Number of days to add
 * @returns {Date}
 */
export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Get an array of dates between start and end (inclusive)
 * @param {Object} interval - Object with start and end dates
 * @param {Date|string} interval.start - Start date
 * @param {Date|string} interval.end - End date
 * @returns {Date[]}
 */
export function eachDayOfInterval({ start, end }) {
  const days = [];
  const current = new Date(start);
  const endDate = new Date(end);
  
  while (current <= endDate) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  
  return days;
}

/**
 * Check if a date/time is in the past
 * @param {Date|string} date - Date to check
 * @returns {boolean}
 */
export function isPast(date) {
  return new Date(date) < new Date();
}

/**
 * Check if a date/time is in the future
 * @param {Date|string} date - Date to check
 * @returns {boolean}
 */
export function isFuture(date) {
  return new Date(date) > new Date();
}

/**
 * Get the start of a day (midnight)
 * @param {Date|string} date - Date
 * @returns {Date}
 */
export function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get the end of a day (23:59:59.999)
 * @param {Date|string} date - Date
 * @returns {Date}
 */
export function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Get a human-readable time until a date
 * @param {Date|string} targetDate - Target date
 * @returns {string}
 */
export function getTimeUntil(targetDate) {
  const target = new Date(targetDate);
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  
  if (diffMs <= 0) return 'Started';
  
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (diffHours > 24) {
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  } else if (diffHours > 0) {
    return `${diffHours}h ${diffMinutes}m`;
  } else {
    return `${diffMinutes}m`;
  }
}

// Export constants for use elsewhere if needed
export const TIMEZONE = APP_TIMEZONE;
export const LOCALE = APP_LOCALE;
